import type { WorkflowInstance } from '@/types/workflows';

/**
 * How the approvals inbox arranges itself — pure, so a test can hold it still.
 *
 * The inbox groups by PROCESS and sorts OLDEST FIRST within each group. Both are
 * deliberate: an approver clears "all the fee waivers" in one pass rather than
 * context-switching per card, and the case that has waited longest surfaces at
 * the top because waiting time — not arrival order in the payload — is what an
 * approval queue owes people. Group order follows the longest-waiting case in
 * each group for the same reason.
 */

export interface InboxGroup {
  processKey: string;
  items: WorkflowInstance[];
}

/** Millisecond timestamp for ordering; an unparseable date sinks to the bottom
 *  rather than jumping the queue. */
function waitingSince(instance: WorkflowInstance): number {
  const time = Date.parse(instance.createdAt);
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}

export function groupInbox(instances: readonly WorkflowInstance[]): InboxGroup[] {
  const byProcess = new Map<string, WorkflowInstance[]>();
  for (const item of instances) {
    const bucket = byProcess.get(item.processKey);
    if (bucket) bucket.push(item);
    else byProcess.set(item.processKey, [item]);
  }

  const groups: InboxGroup[] = [...byProcess.entries()].map(([processKey, items]) => ({
    processKey,
    items: [...items].sort((a, b) => waitingSince(a) - waitingSince(b)),
  }));

  return groups.sort((a, b) => waitingSince(a.items[0]) - waitingSince(b.items[0]));
}

/**
 * "Step 2 of 3", computed from the snapshot rather than trusted blindly:
 * a currentStep past the snapshot's length is clamped so the sentence can never
 * claim step 4 of 3 — a display that would read as data corruption to the user
 * even when it is only an off-by-one upstream.
 */
export function stepProgress(instance: WorkflowInstance): { current: number; total: number } {
  const total = instance.definitionSnapshot.steps.length;
  if (total === 0) return { current: 0, total: 0 };
  return { current: Math.min(Math.max(instance.currentStep, 1), total), total };
}

export type InstanceStepStatus = 'pending' | 'current' | 'done' | 'rejected';

/**
 * How each snapshot step reads on the chain diagram, derived from the recorded
 * decisions rather than from currentStep alone. Decisions outrank the pointer:
 * a step with a REJECT on record draws rejected even if currentStep never moved,
 * because the decision is the fact and the pointer is bookkeeping. A CANCELLED
 * case keeps its decided steps filled and leaves the rest pending — nothing is
 * "current" on a case nobody is waiting on.
 */
export function stepStatuses(instance: WorkflowInstance): InstanceStepStatus[] {
  const { current, total } = stepProgress(instance);
  return instance.definitionSnapshot.steps.map((step, index) => {
    const stepNo = index + 1;
    const decisions = instance.decisions.filter((entry) => entry.stepNo === stepNo);
    if (decisions.some((entry) => entry.decision === 'REJECT')) return 'rejected';
    if (instance.status === 'APPROVED') return 'done';
    if (decisions.filter((entry) => entry.decision === 'APPROVE').length >= step.quorum) return 'done';
    if (stepNo < current) return 'done';
    if (instance.status === 'PENDING' && stepNo === current && total > 0) return 'current';
    return 'pending';
  });
}
